// Whether the browser can reach the network, and the error the API client
// raises instead of trying when it can't.
//
// "Offline" for the app is either of two things, tracked by the CacheManager
// (cacheUtils.ts) and mirrored into the cache slice:
// - the browser says so (navigator.onLine false, the offline event), or
// - the API keeps failing with network errors, timeouts or 5xx while the
//   browser thinks it's online ("cached mode", the API client's failure path
//   feeding recordApiFailure).
// (The browser's word and the offline error class are kast-common's, from
// ./offline; the CacheManager and the read-only mode are the app's own.)
// Either way the app is read-only: the signed-in user's saved data shows, and
// writes are refused with OFFLINE_WRITE_MESSAGE rather than sent.

import {
  NeedsConnectionError,
  isOnline,
  subscribeOnline,
} from "@jkast90/kast-common/offline";
import { formatDateTime } from "@jkast90/kast-common/format";

/** What a refused write says, in the banner and next to disabled saves. */
export const OFFLINE_WRITE_MESSAGE =
  "You're offline. Changes need a connection.";

/**
 * Thrown by the API client for a request it didn't send because the app is
 * offline. A kast-common NeedsConnectionError, so the shared
 * isNeedsConnectionError recognises it too.
 */
export class OfflineError extends NeedsConnectionError {
  readonly offline = true;
  constructor(message = OFFLINE_WRITE_MESSAGE) {
    super(message);
    this.name = "OfflineError";
  }
}

export function isOfflineError(error: unknown): boolean {
  return !!error && (error as { offline?: unknown }).offline === true;
}

/** The browser's own view; true where it can't say (tests, old engines). */
export function browserOnline(): boolean {
  try {
    return isOnline();
  } catch {
    return true;
  }
}

/**
 * Calls `listener(online)` on the browser's online and offline events.
 * Returns the unsubscribe.
 */
export function onBrowserConnectivity(
  listener: (online: boolean) => void,
): () => void {
  return subscribeOnline(() => listener(isOnline()));
}

/** "Offline — showing saved data from <time>", or what to say with nothing saved. */
export function offlineBannerText(
  savedAt: number | null,
  browserOffline: boolean,
  now: number = Date.now(),
): string {
  const lead = browserOffline ? "Offline" : "Can't reach the server";
  if (savedAt === null) return `${lead}: nothing saved on this device yet`;
  // "3:04 PM" today, "Oct 22, 2026, 3:04 PM" otherwise.
  const saved = formatDateTime(savedAt, { todayAsTime: true, now });
  return `${lead} — showing saved data from ${saved}`;
}
